import { useEffect, useState } from 'react';
import type { Language } from '@/utils/i18n';

export type Theme = 'light' | 'dark';

export const SHARED_THEME_KEY = 'haiko-theme';
export const LEGACY_THEME_KEY = 'haiko-tetris-theme';

export function readUrlTheme(): Theme | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const theme = params.get('theme');
    if (theme === 'light' || theme === 'dark') {
      return theme;
    }
  } catch {}
  return null;
}

export function getCurrentTheme(): Theme {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export function useCurrentTheme(): Theme {
  const [theme, setTheme] = useState<Theme>(() => getCurrentTheme());

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const updateTheme = () => setTheme(getCurrentTheme());
    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  return theme;
}

export function applyTheme(theme: Theme) {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.classList.remove('dark');
    document.documentElement.setAttribute('data-theme', 'light');
  }
}

export function persistTheme(theme: Theme) {
  try {
    localStorage.setItem(SHARED_THEME_KEY, theme);
    localStorage.setItem(LEGACY_THEME_KEY, theme);
  } catch {}
}

export function readPersistedTheme(): Theme | null {
  try {
    const shared = localStorage.getItem(SHARED_THEME_KEY);
    if (shared === 'light' || shared === 'dark') return shared;

    const legacy = localStorage.getItem(LEGACY_THEME_KEY);
    if (legacy === 'light' || legacy === 'dark') return legacy;
  } catch {}

  return null;
}

export function readStoredTheme(options?: { includeUrl?: boolean }): Theme {
  if (options?.includeUrl !== false) {
    const urlTheme = readUrlTheme();
    if (urlTheme) {
      persistTheme(urlTheme);
      return urlTheme;
    }
  }

  const persistedTheme = readPersistedTheme();
  if (persistedTheme) return persistedTheme;

  return 'dark';
}

export function syncThemeUrl(theme: Theme) {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  url.searchParams.set('theme', theme);
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function withLang(url: string, lang: Language): string {
  const hasQuery = url.includes('?');
  const hasHash = url.includes('#');

  if (hasHash) {
    const [base, hash] = url.split('#');
    return `${base}${hasQuery ? '&' : '?'}lang=${lang}#${hash}`;
  }

  return `${url}${hasQuery ? '&' : '?'}lang=${lang}`;
}

export function withTheme(url: string, theme: Theme): string {
  const hasHash = url.includes('#');

  if (hasHash) {
    const [base, hash] = url.split('#');
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}theme=${theme}#${hash}`;
  }

  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}theme=${theme}`;
}

export function withLangAndTheme(url: string, lang: Language, theme: Theme): string {
  return withTheme(withLang(url, lang), theme);
}